'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import {
  SupportTicket, TicketCategory, TicketPriority,
  createTicket, getAllTickets, getTicketByNumber, getTicketStats,
  seedDemoTickets, addMessageToTicket,
  CATEGORY_LABELS, STATUS_LABELS, PRIORITY_LABELS,
} from '@/lib/supportTickets';
import { sendWhatsAppNotification } from '@/lib/smsNotification';

type ViewType = 'create' | 'track' | 'my_tickets';

const STATUS_COLORS: Record<string, string> = {
  open: 'bg-blue-100 text-blue-700',
  in_progress: 'bg-yellow-100 text-yellow-700',
  waiting_customer: 'bg-purple-100 text-purple-700',
  resolved: 'bg-green-100 text-green-700',
  closed: 'bg-gray-100 text-gray-600',
};

const PRIORITY_COLORS: Record<string, string> = {
  low: 'bg-gray-100 text-gray-600',
  medium: 'bg-blue-100 text-blue-700',
  high: 'bg-orange-100 text-orange-700',
  urgent: 'bg-red-100 text-red-700',
};

export default function SupportTicketingInteractive() {
  const [view, setView] = useState<ViewType>('create');
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [stats, setStats] = useState({ total: 0, open: 0, inProgress: 0, resolved: 0, urgent: 0, high: 0, waitingCustomer: 0, closed: 0 });
  const [selectedTicket, setSelectedTicket] = useState<SupportTicket | null>(null);
  const [trackNumber, setTrackNumber] = useState('');
  const [trackResult, setTrackResult] = useState<SupportTicket | null>(null);
  const [trackError, setTrackError] = useState('');
  const [submitSuccess, setSubmitSuccess] = useState<SupportTicket | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [form, setForm] = useState({
    customerName: '', customerEmail: '', customerPhone: '',
    subject: '', description: '',
    category: 'general_enquiry' as TicketCategory,
    priority: 'medium' as TicketPriority,
    bookingRef: '',
  });

  useEffect(() => { seedDemoTickets(); refreshData(); }, []);

  function refreshData() {
    setTickets(getAllTickets());
    setStats(getTicketStats());
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setIsSubmitting(true);
    setTimeout(() => {
      const ticket = createTicket({
        customerName: form.customerName, customerEmail: form.customerEmail,
        customerPhone: form.customerPhone || undefined, subject: form.subject,
        description: form.description, category: form.category, priority: form.priority,
        bookingRef: form.bookingRef || undefined,
      });
      sendWhatsAppNotification({ type: 'support_ticket_created', recipientName: form.customerName, recipientPhone: form.customerPhone, ticketId: ticket.ticketNumber });
      setSubmitSuccess(ticket);
      setIsSubmitting(false);
      setForm({ customerName: '', customerEmail: '', customerPhone: '', subject: '', description: '', category: 'general_enquiry', priority: 'medium', bookingRef: '' });
      refreshData();
    }, 800);
  }

  function handleTrack(e: React.FormEvent) {
    e.preventDefault();
    setTrackError('');
    setTrackResult(null);
    const found = getTicketByNumber(trackNumber.trim().toUpperCase());
    if (found) { setTrackResult(found); }
    else { setTrackError('No ticket found with that number. Please check and try again.'); }
  }

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-10">
        <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-bold mb-4">
          <Icon name="ChatBubbleLeftRightIcon" size={16} />
          Customer Support
        </div>
        <h1 className="font-heading text-4xl md:text-5xl font-black text-foreground mb-3">How Can We Help?</h1>
        <p className="text-lg text-muted-foreground max-w-2xl mx-auto">Create a support ticket and our team will respond within 2 hours. Track your existing tickets anytime.</p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
        {[
          { label: 'Open Tickets', value: stats.open, color: 'text-blue-600', bg: 'bg-blue-50' },
          { label: 'In Progress', value: stats.inProgress, color: 'text-yellow-600', bg: 'bg-yellow-50' },
          { label: 'Resolved', value: stats.resolved, color: 'text-green-600', bg: 'bg-green-50' },
          { label: 'Urgent', value: stats.urgent, color: 'text-red-600', bg: 'bg-red-50' },
        ].map(s => (
          <div key={s.label} className={`${s.bg} rounded-xl p-4 text-center`}>
            <p className={`text-2xl font-black ${s.color}`}>{s.value}</p>
            <p className="text-xs text-muted-foreground font-medium mt-1">{s.label}</p>
          </div>
        ))}
      </div>

      <div className="flex gap-2 mb-8 bg-surface rounded-xl p-1">
        {([{ id: 'create', label: 'Create Ticket', icon: 'PlusCircleIcon' }, { id: 'track', label: 'Track Ticket', icon: 'MagnifyingGlassIcon' }, { id: 'my_tickets', label: 'All Tickets', icon: 'ListBulletIcon' }] as { id: ViewType; label: string; icon: string }[]).map(tab => (
          <button key={tab.id} onClick={() => { setView(tab.id); setSubmitSuccess(null); setTrackResult(null); setTrackError(''); }}
            className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-lg text-sm font-bold transition-all ${
              view === tab.id ? 'bg-white shadow text-primary' : 'text-muted-foreground hover:text-foreground'
            }`}>
            <Icon name={tab.icon as any} size={16} />
            <span className="hidden sm:inline">{tab.label}</span>
          </button>
        ))}
      </div>

      {view === 'create' && (
        <div className="bg-white rounded-2xl shadow-brand border border-border p-6 md:p-8">
          {submitSuccess ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name="CheckCircleIcon" size={32} className="text-green-600" />
              </div>
              <h3 className="font-heading text-2xl font-black text-foreground mb-2">Ticket Created!</h3>
              <p className="text-muted-foreground mb-4">Your ticket number is <strong className="text-primary font-mono text-lg">{submitSuccess.ticketNumber}</strong></p>
              <p className="text-sm text-muted-foreground mb-6">We&apos;ll respond within 2 hours. A WhatsApp notification has been sent.</p>
              <div className="flex gap-3 justify-center flex-wrap">
                <button onClick={() => { setTrackNumber(submitSuccess.ticketNumber); setView('track'); }} className="bg-primary text-white px-6 py-3 rounded-xl font-bold hover:bg-primary/90 transition-colors">Track This Ticket</button>
                <button onClick={() => setSubmitSuccess(null)} className="border border-border px-6 py-3 rounded-xl font-bold hover:bg-surface transition-colors">Create Another</button>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              <h2 className="font-heading text-xl font-black text-foreground mb-1">Submit a Support Request</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-bold text-foreground mb-1">Full Name *</label>
                  <input type="text" required value={form.customerName} onChange={e => setForm(f => ({ ...f, customerName: e.target.value }))} className="w-full border border-border rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30" placeholder="Your full name" />
                </div>
                <div>
                  <label className="block text-sm font-bold text-foreground mb-1">Email Address *</label>
                  <input type="email" required value={form.customerEmail} onChange={e => setForm(f => ({ ...f, customerEmail: e.target.value }))} className="w-full border border-border rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30" placeholder="your@email.com" />
                </div>
                <div>
                  <label className="block text-sm font-bold text-foreground mb-1">Phone (optional)</label>
                  <input type="tel" value={form.customerPhone} onChange={e => setForm(f => ({ ...f, customerPhone: e.target.value }))} className="w-full border border-border rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30" placeholder="07700 900 123" />
                </div>
                <div>
                  <label className="block text-sm font-bold text-foreground mb-1">Booking Reference</label>
                  <input type="text" value={form.bookingRef} onChange={e => setForm(f => ({ ...f, bookingRef: e.target.value }))} className="w-full border border-border rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30" placeholder="DFF123456 (if applicable)" />
                </div>
                <div>
                  <label className="block text-sm font-bold text-foreground mb-1">Category *</label>
                  <select value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value as TicketCategory }))} className="w-full border border-border rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 bg-white">
                    {Object.entries(CATEGORY_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-bold text-foreground mb-1">Priority</label>
                  <select value={form.priority} onChange={e => setForm(f => ({ ...f, priority: e.target.value as TicketPriority }))} className="w-full border border-border rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 bg-white">
                    {Object.entries(PRIORITY_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-bold text-foreground mb-1">Subject *</label>
                <input type="text" required value={form.subject} onChange={e => setForm(f => ({ ...f, subject: e.target.value }))} className="w-full border border-border rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30" placeholder="Brief description of your issue" />
              </div>
              <div>
                <label className="block text-sm font-bold text-foreground mb-1">Description *</label>
                <textarea required rows={5} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} className="w-full border border-border rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none" placeholder="Please provide as much detail as possible..." />
              </div>
              <button type="submit" disabled={isSubmitting} className="w-full bg-primary text-white py-4 rounded-xl font-black text-base hover:bg-primary/90 transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
                {isSubmitting ? (<><div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>Submitting...</>) : (<><Icon name="PaperAirplaneIcon" size={20} />Submit Support Ticket</>)}
              </button>
            </form>
          )}
        </div>
      )}

      {view === 'track' && (
        <div className="bg-white rounded-2xl shadow-brand border border-border p-6 md:p-8">
          <h2 className="font-heading text-xl font-black text-foreground mb-4">Track Your Ticket</h2>
          <form onSubmit={handleTrack} className="flex gap-3 mb-6">
            <input type="text" value={trackNumber} onChange={e => setTrackNumber(e.target.value)} className="flex-1 border border-border rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 font-mono" placeholder="Enter ticket number e.g. TKT-12345" />
            <button type="submit" className="bg-primary text-white px-6 py-3 rounded-xl font-bold hover:bg-primary/90 transition-colors">Track</button>
          </form>
          {trackError && <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700 text-sm mb-4">{trackError}</div>}
          {trackResult && <TicketDetail ticket={trackResult} onRefresh={refreshData} />}
        </div>
      )}

      {view === 'my_tickets' && (
        <div className="space-y-4">
          {tickets.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Icon name="InboxIcon" size={48} className="mx-auto mb-3 opacity-30" />
              <p>No tickets yet</p>
            </div>
          ) : (
            tickets.map(ticket => (
              <div key={ticket.id} className="bg-white rounded-xl border border-border p-5 hover:shadow-md transition-shadow cursor-pointer" onClick={() => setSelectedTicket(selectedTicket?.id === ticket.id ? null : ticket)}>
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className="font-mono text-xs text-muted-foreground">{ticket.ticketNumber}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-bold ${STATUS_COLORS[ticket.status]}`}>{STATUS_LABELS[ticket.status]}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-bold ${PRIORITY_COLORS[ticket.priority]}`}>{PRIORITY_LABELS[ticket.priority]}</span>
                    </div>
                    <p className="font-bold text-foreground truncate">{ticket.subject}</p>
                    <p className="text-sm text-muted-foreground">{ticket.customerName} · {CATEGORY_LABELS[ticket.category]}</p>
                  </div>
                  <div className="text-right text-xs text-muted-foreground whitespace-nowrap">{new Date(ticket.createdAt).toLocaleDateString('en-GB')}</div>
                </div>
                {selectedTicket?.id === ticket.id && (
                  <div className="mt-4 pt-4 border-t border-border">
                    <TicketDetail ticket={ticket} onRefresh={refreshData} />
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      )}

      <div className="mt-8 bg-gradient-to-r from-primary/5 to-secondary/5 rounded-2xl p-6 border border-primary/10">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          <div>
            <p className="font-bold text-foreground">Need urgent help?</p>
            <p className="text-sm text-muted-foreground">Call or WhatsApp us directly for immediate assistance</p>
          </div>
          <div className="flex gap-3">
            <a href="tel:02046152876" className="flex items-center gap-2 bg-primary text-white px-5 py-3 rounded-xl font-bold text-sm hover:bg-primary/90 transition-colors">
              <Icon name="PhoneIcon" size={16} />020 4615 2876
            </a>
            <a href="https://wa.me/442046152876" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 bg-green-600 text-white px-5 py-3 rounded-xl font-bold text-sm hover:bg-green-700 transition-colors">
              <Icon name="ChatBubbleLeftIcon" size={16} />WhatsApp
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

function TicketDetail({ ticket, onRefresh }: { ticket: SupportTicket; onRefresh: () => void }) {
  const [replyText, setReplyText] = useState('');
  const [isSending, setIsSending] = useState(false);

  function handleReply(e: React.FormEvent) {
    e.preventDefault();
    if (!replyText.trim()) return;
    setIsSending(true);
    setTimeout(() => {
      addMessageToTicket(ticket.id, replyText, ticket.customerName, 'customer');
      setReplyText('');
      setIsSending(false);
      onRefresh();
    }, 500);
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
        <div><p className="text-muted-foreground text-xs">Ticket #</p><p className="font-mono font-bold">{ticket.ticketNumber}</p></div>
        <div><p className="text-muted-foreground text-xs">Category</p><p className="font-bold">{CATEGORY_LABELS[ticket.category]}</p></div>
        <div><p className="text-muted-foreground text-xs">Created</p><p className="font-bold">{new Date(ticket.createdAt).toLocaleDateString('en-GB')}</p></div>
        {ticket.bookingRef && <div><p className="text-muted-foreground text-xs">Booking Ref</p><p className="font-mono font-bold">{ticket.bookingRef}</p></div>}
      </div>
      <div className="space-y-3 max-h-64 overflow-y-auto">
        {ticket.messages.filter(m => !m.isInternal).map(msg => (
          <div key={msg.id} className={`rounded-xl p-3 text-sm ${
            msg.authorRole === 'customer' ? 'bg-blue-50 border border-blue-100 ml-4'
            : msg.authorRole === 'agent'? 'bg-green-50 border border-green-100 mr-4' :'bg-gray-50 border border-gray-100 text-center text-xs text-muted-foreground'
          }`}>
            {msg.authorRole !== 'system' && <p className="font-bold text-xs mb-1">{msg.authorName} · {new Date(msg.timestamp).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}</p>}
            <p>{msg.content}</p>
          </div>
        ))}
      </div>
      {ticket.status !== 'resolved' && ticket.status !== 'closed' && (
        <form onSubmit={handleReply} className="flex gap-2">
          <input type="text" value={replyText} onChange={e => setReplyText(e.target.value)} className="flex-1 border border-border rounded-xl px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30" placeholder="Add a reply..." />
          <button type="submit" disabled={isSending || !replyText.trim()} className="bg-primary text-white px-4 py-2 rounded-xl font-bold text-sm hover:bg-primary/90 transition-colors disabled:opacity-50">Send</button>
        </form>
      )}
    </div>
  );
}
